/**
 * Auth token storage + fetch wrapper for the wedding site.
 *
 * Two independent tokens:
 *   - site_token:  set by SiteGate after guests enter the invitation PIN
 *   - admin_token: set by Admin.js after the couple enters the admin PIN
 *
 * Every API call goes through `apiFetch()` / `authedFetch()` which auto-attaches
 * the correct Authorization header and, on 401, clears the offending token so
 * the UI redirects to the appropriate PIN gate.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SITE_TOKEN_KEY = "wed_site_token";
export const ADMIN_TOKEN_KEY = "wed_admin_token";

export const getSiteToken = () => localStorage.getItem(SITE_TOKEN_KEY);
export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);

export const setSiteToken = (t) => localStorage.setItem(SITE_TOKEN_KEY, t);
export const setAdminToken = (t) => localStorage.setItem(ADMIN_TOKEN_KEY, t);

export const clearSiteToken = () => localStorage.removeItem(SITE_TOKEN_KEY);
export const clearAdminToken = () => localStorage.removeItem(ADMIN_TOKEN_KEY);

/**
 * Log in with the given passcode and role. Stores the returned JWT.
 * Returns { ok, error? }.
 */
export async function login(role, passcode) {
  const endpoint = role === "admin" ? "admin-login" : "site-login";
  try {
    const res = await fetch(`${API_URL}/api/auth/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ passcode }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: data.detail || "Login failed", status: res.status };
    }
    if (role === "admin") setAdminToken(data.token);
    else setSiteToken(data.token);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: "Network error. Please try again." };
  }
}

/**
 * Perform an authenticated fetch. Auto-attaches the Bearer token for the
 * caller-specified role ("site" or "admin"). On 401 responses, clears the
 * matching token so the caller's UI can redirect to the PIN gate.
 */
export async function authedFetch(role, path, options = {}) {
  const token = role === "admin" ? getAdminToken() : getSiteToken();
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const res = await fetch(`${API_URL}${path}`, { ...options, headers });
  if (res.status === 401) {
    if (role === "admin") clearAdminToken();
    else clearSiteToken();
  }
  return res;
}

/**
 * Convenience helper: JSON POST with auth.
 */
export async function authedPost(role, path, body) {
  return authedFetch(role, path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export { API_URL };
